import React from 'react';
import { Card, Col, List, Row, Space, Statistic, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';

const { Text } = Typography;

export interface EpcWbsLibraryCatalogueSummaryProps {
  libraryId: string;
  onSelectPackage?: (packageId: string) => void;
}

export const EpcWbsLibraryCatalogueSummary: React.FC<EpcWbsLibraryCatalogueSummaryProps> = ({
  libraryId,
  onSelectPackage,
}) => {
  const analyticsQ = useQuery({
    queryKey: ['epc-wbs-lib-analytics', libraryId],
    queryFn: () => epcWbsLibraryService.getLibraryAnalytics(libraryId),
    enabled: !!libraryId,
    staleTime: 30_000,
  });

  const data = analyticsQ.data;
  if (!data) return null;

  return (
    <Card size="small" style={{ marginBottom: 16 }} loading={analyticsQ.isLoading}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={12} sm={8} md={4}>
          <Statistic title="Packages" value={data.packageCount} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Statistic title="WBS nodes" value={data.wbsNodeCount} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Statistic title="CBS templates" value={data.costTemplateCount} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Statistic title="Estimates using" value={data.estimateCount} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Statistic title="Revision plugs" value={data.revisionCount} />
        </Col>
        <Col xs={24} md={8}>
          {data.topPackages.length > 0 ? (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Most used packages
              </Text>
              <List
                size="small"
                dataSource={data.topPackages}
                renderItem={(row) => (
                  <List.Item style={{ padding: '4px 0' }}>
                    <Space size={6} wrap>
                      {onSelectPackage ? (
                        <ButtonLink onClick={() => onSelectPackage(row.packageId)}>
                          {row.code}
                        </ButtonLink>
                      ) : (
                        <Text strong>{row.code}</Text>
                      )}
                      <Text type="secondary" ellipsis style={{ maxWidth: 140 }}>
                        {row.name}
                      </Text>
                      {row.estimatePlugCount > 0 ? (
                        <Tag color="geekblue">{row.estimatePlugCount} estimates</Tag>
                      ) : (
                        <Tag>unused</Tag>
                      )}
                    </Space>
                  </List.Item>
                )}
              />
            </div>
          ) : (
            <Text type="secondary">No packages yet</Text>
          )}
        </Col>
      </Row>
    </Card>
  );
};

function ButtonLink({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <Text strong style={{ color: '#1677ff', cursor: 'pointer' }} onClick={onClick}>
      {children}
    </Text>
  );
}
